import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PLATFORM_URL } from "@/config/auth";
import { isValidWorkspaceRedirect } from "@/lib/domainUtils";

/**
 * SSO Callback Page for the Identity Module
 * 
 * Handles the redirect from the IdP after SSO authentication.
 * Key differences from the platform's SSOCallbackPage:
 * - After establishing the session, redirects to the workspace (platform) URL
 * - Uses cross-domain redirect instead of internal navigation
 * - Preserves the original redirect_uri from the PAR flow
 */
export default function SSOCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Get the target redirect URL after successful authentication.
   * Priority:
   * 1. redirect_uri from URL params (from PAR flow)
   * 2. Default to platform URL with session-transfer
   */
  const getWorkspaceRedirectUrl = (): string => {
    const redirectUri = searchParams.get('redirect_uri');
    
    // Validate redirect_uri is a valid workspace domain
    if (redirectUri && isValidWorkspaceRedirect(redirectUri)) {
      console.log('[SSOCallbackPage] Using redirect_uri from params:', redirectUri);
      return redirectUri;
    }
    
    // Fallback to platform with session transfer
    const platformUrl = `${PLATFORM_URL}/accounts/session-transfer`;
    console.log('[SSOCallbackPage] Using default platform URL:', platformUrl);
    return platformUrl;
  };

  useEffect(() => {
    const handleCallback = async () => {
      const ssoToken = searchParams.get('sso_token');
      const ssoError = searchParams.get('sso_error');
      
      // Check popup mode from URL params (cross-origin compatible) or sessionStorage
      const isPopupModeFromUrl = searchParams.get('popup_mode') === 'true';
      const isPopupModeFromSession = sessionStorage.getItem('sso_popup_mode') === 'true';
      const isPopupMode = isPopupModeFromUrl || isPopupModeFromSession;
      
      // Get opener origin from URL params for cross-origin postMessage
      const openerOrigin = searchParams.get('opener_origin');

      // Clean up popup mode flag
      sessionStorage.removeItem('sso_popup_mode');

      // FIRST: Check for hash fragment tokens from Supabase magic link
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        console.log('[SSOCallbackPage] Detected tokens in hash fragment, setting session...');
        
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (data.session) {
            console.log('[SSOCallbackPage] Session established from hash tokens');
            // Mark this as an SSO session to bypass local 2FA check
            sessionStorage.setItem('sso_authenticated', 'true');
            
            // Store SSO config_id for OIDC logout if present
            const configId = searchParams.get('config_id');
            if (configId) {
              sessionStorage.setItem('sso_config_id', configId);
            }
            
            setStatus('success');
            
            if (isPopupMode && window.opener) {
              const targetOrigin = openerOrigin || window.location.origin;
              window.opener.postMessage({
                type: 'SSO_CALLBACK_RESULT',
                success: true,
                ssoToken: 'session_established',
              }, targetOrigin);

              localStorage.setItem('sso_callback_result', JSON.stringify({
                success: true,
                ssoToken: 'session_established',
              }));

              setTimeout(() => window.close(), 1000);
            } else {
              // Identity module: redirect to workspace with tokens for cross-domain transfer
              // Skip success status - let SessionTransferPage show the success message
              const targetUrl = getWorkspaceRedirectUrl();
              const hashParams = new URLSearchParams();
              hashParams.set('access_token', data.session.access_token);
              hashParams.set('refresh_token', data.session.refresh_token);
              hashParams.set('sso_session', 'true');
              
              // Preserve popup mode params for the target page
              if (isPopupMode) {
                hashParams.set('popup_mode', 'true');
                if (openerOrigin) {
                  hashParams.set('opener_origin', openerOrigin);
                }
              }
              
              const redirectUrl = `${targetUrl}#${hashParams.toString()}`;
              console.log('[SSOCallbackPage] Redirecting to workspace with tokens:', targetUrl);
              // Redirect immediately - no success display needed
              window.location.href = redirectUrl;
            }
            return;
          }
          
          if (error) {
            console.error('[SSOCallbackPage] Failed to set session:', error);
            throw error;
          }
        } catch (e) {
          console.error('[SSOCallbackPage] Error setting session from hash:', e);
          setStatus('error');
          setErrorMessage('Failed to establish session. Please try again.');
          
          if (!isPopupMode) {
            setTimeout(() => navigate('/', { replace: true }), 2000);
          }
          return;
        }
      }

      if (ssoError) {
        setStatus('error');
        setErrorMessage(ssoError);

        if (isPopupMode && window.opener) {
          const targetOrigin = openerOrigin || window.location.origin;
          window.opener.postMessage({
            type: 'SSO_CALLBACK_RESULT',
            success: false,
            error: ssoError,
          }, targetOrigin);

          localStorage.setItem('sso_callback_result', JSON.stringify({
            success: false,
            error: ssoError,
          }));

          setTimeout(() => window.close(), 1500);
        } else {
          // Redirect to login page with error
          setTimeout(() => navigate(`/?sso_error=${encodeURIComponent(ssoError)}`, { replace: true }), 1500);
        }
        return;
      }

      if (ssoToken) {
        // SSO was successful, session should be established by Supabase magic link
        // Verify we have a session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          // Mark this as an SSO session to bypass local 2FA check
          sessionStorage.setItem('sso_authenticated', 'true');
          
          // Store SSO config_id for OIDC logout if present
          const configId = searchParams.get('config_id');
          if (configId) {
            sessionStorage.setItem('sso_config_id', configId);
          }
          
          if (isPopupMode && window.opener) {
            setStatus('success');
            const targetOrigin = openerOrigin || window.location.origin;
            window.opener.postMessage({
              type: 'SSO_CALLBACK_RESULT',
              success: true,
              ssoToken: 'session_established',
            }, targetOrigin);

            localStorage.setItem('sso_callback_result', JSON.stringify({
              success: true,
              ssoToken: 'session_established',
            }));

            setTimeout(() => window.close(), 1000);
          } else {
            // Identity module: redirect to workspace with tokens for cross-domain transfer
            // Skip success status - let SessionTransferPage show the success message
            const targetUrl = getWorkspaceRedirectUrl();
            const hashParams = new URLSearchParams();
            hashParams.set('access_token', session.access_token);
            hashParams.set('refresh_token', session.refresh_token);
            hashParams.set('sso_session', 'true');
            
            // Preserve popup mode params for the target page
            if (isPopupMode) {
              hashParams.set('popup_mode', 'true');
              if (openerOrigin) {
                hashParams.set('opener_origin', openerOrigin);
              }
            }
            
            const redirectUrl = `${targetUrl}#${hashParams.toString()}`;
            console.log('[SSOCallbackPage] Session exists, redirecting to workspace with tokens:', targetUrl);
            // Redirect immediately - no success display needed
            window.location.href = redirectUrl;
          }
        } else {
          // Session not established yet, wait for auth state change
          const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session) {
              // Mark this as an SSO session to bypass local 2FA check
              sessionStorage.setItem('sso_authenticated', 'true');
              
              // Store SSO config_id for OIDC logout if present
              const configId = searchParams.get('config_id');
              if (configId) {
                sessionStorage.setItem('sso_config_id', configId);
              }
              
              subscription.unsubscribe();

              if (isPopupMode && window.opener) {
                setStatus('success');
                const targetOrigin = openerOrigin || window.location.origin;
                window.opener.postMessage({
                  type: 'SSO_CALLBACK_RESULT',
                  success: true,
                  ssoToken: 'session_established',
                }, targetOrigin);

                localStorage.setItem('sso_callback_result', JSON.stringify({
                  success: true,
                  ssoToken: 'session_established',
                }));

                setTimeout(() => window.close(), 1000);
              } else {
                // Identity module: redirect to workspace with tokens for cross-domain transfer
                // Skip success status - let SessionTransferPage show the success message
                const targetUrl = getWorkspaceRedirectUrl();
                const hashParams = new URLSearchParams();
                hashParams.set('access_token', session.access_token);
                hashParams.set('refresh_token', session.refresh_token);
                hashParams.set('sso_session', 'true');
                
                // Preserve popup mode params for the target page
                if (isPopupMode) {
                  hashParams.set('popup_mode', 'true');
                  if (openerOrigin) {
                    hashParams.set('opener_origin', openerOrigin);
                  }
                }
                
                const redirectUrl = `${targetUrl}#${hashParams.toString()}`;
                console.log('[SSOCallbackPage] Auth state changed, redirecting to workspace with tokens:', targetUrl);
                // Redirect immediately - no success display needed
                window.location.href = redirectUrl;
              }
            }
          });

          // Timeout fallback
          setTimeout(() => {
            subscription.unsubscribe();
            if (status === 'processing') {
              setStatus('error');
              setErrorMessage('Session establishment timed out. Please try again.');
              if (!isPopupMode) {
                setTimeout(() => navigate('/', { replace: true }), 2000);
              }
            }
          }, 10000);
        }
        return;
      }

      // No token or error - something went wrong
      setStatus('error');
      setErrorMessage('No authentication data received');
      
      if (isPopupMode && window.opener) {
        const targetOrigin = openerOrigin || window.location.origin;
        window.opener.postMessage({
          type: 'SSO_CALLBACK_RESULT',
          success: false,
          error: 'No authentication data received',
        }, targetOrigin);
        setTimeout(() => window.close(), 1500);
      } else {
        setTimeout(() => navigate('/', { replace: true }), 1500);
      }
    };

    handleCallback();
  }, [searchParams, navigate, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <h2 className="text-xl font-semibold">Processing SSO Login</h2>
            <p className="text-muted-foreground">Please wait while we complete your sign-in...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-green-500" />
            <h2 className="text-xl font-semibold">Sign-in Successful</h2>
            <p className="text-muted-foreground">
              {window.opener ? 'This window will close automatically...' : 'Redirecting to your workspace...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Sign-in Failed</h2>
            <p className="text-muted-foreground">{errorMessage || 'An error occurred during sign-in'}</p>
            {!window.opener && (
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-4 text-primary hover:underline"
              >
                Return to login
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
